import React from "react";
import { Badge, MantineColor, MantineSize } from "@mantine/core";

/**
 * Centralized Badge utilities and components
 *
 * This module consolidates badge styling and semantics so pages can reuse
 * consistent visuals and behavior (e.g., contrast rules for bright colors).
 */

// =====================
// User Flags (Admin)
// =====================

/** Canonical set of user administrative flags. */
export const USER_FLAG_OPTIONS = ["Suspended", "Banned", "Watchlist"] as const;
export type UserFlag = typeof USER_FLAG_OPTIONS[number];

/**
 * Returns display metadata for a given user flag.
 * - color: Mantine theme color name
 * - label: human-friendly label for display contexts
 * - bright: whether the color is considered bright (adjust text color for contrast)
 */
export function getUserFlagMeta(type: UserFlag): { color: MantineColor; label: string; bright: boolean } {
  switch (type) {
    case "Banned":
      return { color: "red", label: "Banned", bright: false };
    case "Suspended":
      return { color: "yellow", label: "Suspended", bright: true };
    case "Watchlist":
      // In admin UI we show this as "Under Investigation"
      return { color: "grape", label: "Under Investigation", bright: false };
    default:
      // Exhaustive, but keep a safe fallback
      return { color: "gray", label: String(type), bright: false } as any;
  }
}

export type UserFlagBadgeProps = {
  /** The canonical flag type */
  type: UserFlag;
  /** Optional: override label for specific contexts */
  labelOverride?: string;
  /** Optional: override the Mantine color */
  colorOverride?: MantineColor;
  /** Optional: override variant (defaults to "filled") */
  variantOverride?: "filled" | "light" | "outline" | "dot" | "transparent" | "gradient";
  /** Optional: text color override (e.g., to tweak contrast) */
  textColorOverride?: string;
  /** Optional: size passthrough */
  size?: MantineSize;
};

/**
 * UserFlagBadge
 * Reusable badge for user flags with contrast-aware text for bright hues.
 * Keeps a colored background (filled) by default to communicate state.
 */
export function UserFlagBadge(props: UserFlagBadgeProps) {
  const { type, labelOverride, colorOverride, variantOverride, textColorOverride, size } = props;
  const meta = getUserFlagMeta(type);
  const color = colorOverride ?? meta.color;
  const variant = variantOverride ?? "filled";

  // For filled badges on bright colors, prefer a dark gray text for contrast (avoid pure black).
  const computedTextColor = textColorOverride ?? (variant === "filled" && meta.bright ? "gray.8" : undefined);
  const label = labelOverride ?? meta.label;

  return (
    <Badge color={color} variant={variant} c={computedTextColor} size={size}>
      {label}
    </Badge>
  );
}

/**
 * Convenience group renderer if multiple flags are provided.
 */
export function UserFlagsGroup({ flags, size }: { flags: readonly UserFlag[]; size?: MantineSize }) {
  return (
    <>
      {flags.map((f) => (
        <UserFlagBadge key={f} type={f} size={size} />
      ))}
    </>
  );
}
